import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Alert, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { FaChevronDown, FaChevronUp, FaQuestionCircle } from "react-icons/fa";
import { useCreateGameDataMutation } from "../../slices/gameDataApiSlice";
import { useCreateSquadDataMutation, useGetSquadDataQuery } from "../../slices/squadDataApiSlice";
import { useUploadGameCSVMutation } from "../../slices/gameDataApiSlice";
import WindDirectionPreview from "./WindDirectionPreview";
import GameCSVModal from "./GameCSVModal";

const COMPETITIONS = ["League", "Championship", "Challenge", "Other"];
const GAME_TYPES = ["Mens", "Ladies"];
const TEAM_COLORS = [
  "red",
  "blue",
  "brown",
  "cyan",
  "green",
  "orange",
  "purple",
];
const WIND_ANGLES = Array.from({ length: 24 }, (_, i) => String(i * 15));
const THROW_IN_TIMES = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});
const formatColorLabel = (color) => `${String(color).charAt(0).toUpperCase()}${String(color).slice(1)}`;
const DEFAULT_NEW_PLAYER_POSITION = "Full-Back";
const normalizeTeamName = (value) => String(value || "").trim().toLowerCase();
const TOAST_OPTIONS = { autoClose: 2500 };

const getPreviousDayISO = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const hhmmssToSeconds = value => {
  if (!/^\d{1,2}:[0-5]\d:[0-5]\d$/.test(value || "")) return NaN;
  const [hh, mm, ss] = value.split(":").map(Number)
  if (ss > 59 || mm > 59) {
    return NaN
  } 

  return ((hh * 60 * 60) + (mm * 60) + ss)
}

const AddGameModal = ({ show, onHide }) => {
  const basicsRef = useRef(null);
  const homeTeamRef = useRef(null);
  const awayTeamRef = useRef(null);
  const videoTimeRef = useRef(null);
  const windRef = useRef(null);
  const csvRef = useRef(null);
  const [formData, setFormData] = useState({
    GameDate: getPreviousDayISO(),
    ThrowInTime: "20:00",
    Competition: "League",
    GameType: "Mens",
    HomeTeam: "",
    HomeTeamColor: "red",
    AwayTeam: "",
    AwayTeamColor: "blue",
    FirstHalfVideoStart: "00:00:00",
    SecondHalfVideoStart: "00:00:00",
    County: "",
    Town: "",
    WindAngle: "0",
    WindStrength: "0",
  });

  const [loadingWind, setLoadingWind] = useState(false);
  const [windMessage, setWindMessage] = useState("");
  const [showWindLookup, setShowWindLookup] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [pendingCsvFiles, setPendingCsvFiles] = useState([]);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const [uploadGameCSV] = useUploadGameCSVMutation();

  const [createGame] = useCreateGameDataMutation();
  const [createSquadData] = useCreateSquadDataMutation();
  const { data: squadData = { entries: [] } } = useGetSquadDataQuery();
  const teamOptions = useMemo(() => {
    const allTeams = (squadData.entries || []).map((entry) => entry.team);
    return [...new Set(allTeams.filter(Boolean).map((team) => String(team).trim()))].sort((a, b) =>
      String(a).localeCompare(String(b)),
    );
  }, [squadData.entries]);
  const teamLookup = useMemo(() => {
    const map = new Map();
    teamOptions.forEach((team) => {
      map.set(normalizeTeamName(team), team);
    });
    return map;
  }, [teamOptions]);


  /*
  const awayColorOptions = useMemo(
    () => TEAM_COLORS.filter((color) => color !== formData.HomeTeamColor),
    [formData.HomeTeamColor],
  );
  const awayTeamOptions = useMemo(
    () => teamOptions.filter((team) => team !== formData.HomeTeam),
    [teamOptions, formData.HomeTeam],
  );
  */
  //changed to allow teams to play themselves
  const awayColorOptions = TEAM_COLORS
  const awayTeamOptions = teamOptions

  useEffect(() => {
    if (!show) {
      setFormData({
        GameDate: getPreviousDayISO(),
        ThrowInTime: "20:00",
        Competition: "League",
        GameType: "Mens",
        HomeTeam: "",
        HomeTeamColor: "red",
        AwayTeam: "",
        AwayTeamColor: "blue",
        FirstHalfVideoStart: "00:00:00",
        SecondHalfVideoStart: "00:00:00",
        County: "",
        Town: "",
        WindAngle: "0",
        WindStrength: "0",
      });
      setLoadingWind(false);
      setWindMessage("");
      setShowWindLookup(false);
      setShowCsvUpload(false);
      setPendingCsvFiles([]);
      setShowTutorial(false);
      setTutorialStepIndex(0);
      setTutorialRect(null);
    }
  }, [show]);

  const tutorialSteps = useMemo(
    () => [
      {
        title: "Basics",
        body: "Start with the game date, throw-in time, and competition. These define the match context before any team or shot data is added.",
        ref: basicsRef,
      },
      {
        title: "Home Team",
        body: "Set the home team name and color here. If the team does not already exist, the app can create the team and a default player record.",
        ref: homeTeamRef,
      },
      {
        title: "Away Team",
        body: "Set the away team details here. This completes the match pairing used throughout analysis and data entry.",
        ref: awayTeamRef,
      },
      {
        title: "Video Time Anchors",
        body: "These are critical. First-half and second-half video start times are used later to convert entered shot video timestamps into in-game time.",
        ref: videoTimeRef,
      },
      {
        title: "Wind Details",
        body: "Wind direction and strength feed the match context. You can enter them manually or use the location lookup to fill wind speed.",
        ref: windRef,
      },
      {
        title: "CSV Option",
        body: "This optional section lets you attach a CSV right after creating the game, which is useful if the shot data already exists externally.",
        ref: csvRef,
      },
    ],
    [],
  );

  const updateTutorialRect = useCallback(() => {
    if (!showTutorial) {
      setTutorialRect(null);
      return;
    }
    const step = tutorialSteps[tutorialStepIndex];
    const node = step?.ref?.current;
    if (!node) {
      setTutorialRect(null);
      return;
    }
    const rect = node.getBoundingClientRect();
    setTutorialRect({
      top: Math.max(8, rect.top - 8),
      left: Math.max(8, rect.left - 8),
      width: rect.width + 16,
      height: rect.height + 16,
    });
  }, [showTutorial, tutorialStepIndex, tutorialSteps]);

  useEffect(() => {
    if (!showTutorial) return undefined;
    const step = tutorialSteps[tutorialStepIndex];
    step?.ref?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    const refresh = () => updateTutorialRect();
    refresh();
    const timeoutId = window.setTimeout(refresh, 220);
    const secondTimeoutId = window.setTimeout(refresh, 420);
    const intervalId = window.setInterval(refresh, 70);
    const stopIntervalId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 800);
    window.addEventListener("resize", refresh);
    window.addEventListener("scroll", refresh, true);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(secondTimeoutId);
      window.clearTimeout(stopIntervalId);
      window.clearInterval(intervalId);
      window.removeEventListener("resize", refresh);
      window.removeEventListener("scroll", refresh, true);
    };
  }, [showTutorial, tutorialStepIndex, tutorialSteps, updateTutorialRect]);

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialRect(null);
  };

  const nextTutorialStep = () => {
    if (tutorialStepIndex >= tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStepIndex((prev) => prev + 1);
  };

  const prevTutorialStep = () => {
    setTutorialStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const getTutorialCardStyle = () => {
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const gap = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const placeBelow =
      tutorialRect.top + tutorialRect.height + 220 + gap < viewportHeight;
    const top = placeBelow
      ? tutorialRect.top + tutorialRect.height + gap
      : Math.max(16, tutorialRect.top - 220 - gap);
    const preferredLeft = tutorialRect.left;
    const maxLeft = Math.max(16, viewportWidth - cardWidth - 16);
    const left = Math.min(Math.max(16, preferredLeft), maxLeft);
    return {
      position: "fixed",
      top,
      left,
      width: cardWidth,
      zIndex: 1062,
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
      transition: "top 160ms ease, left 160ms ease",
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "HomeTeamColor") {
        const next = { ...prev, HomeTeamColor: value };
        if (next.AwayTeamColor === value) {
          const fallbackAway = TEAM_COLORS.find((c) => c !== value) || "";
          next.AwayTeamColor = fallbackAway;
        }
        return next;
      }
      if (name === "HomeTeam") {
        return { ...prev, HomeTeam: value };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleGetWindSpeed = async () => {
    const { County, Town, GameDate, ThrowInTime } = formData;
    if (!County || !Town || !GameDate || !ThrowInTime) {
      toast.error("To auto-fill wind speed, enter county and city first.", TOAST_OPTIONS);
      return;
    }

    setLoadingWind(true);
    setWindMessage("");

    try {
      const geoRes = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
          `${Town}, ${County}`,
        )}`,
        { headers: { "User-Agent": "ShotArc/1.0" } },
      );
      if (!geoRes.ok) throw new Error(`Geocode failed (${geoRes.status})`);

      const geoJson = await geoRes.json();
      if (!geoJson.length) throw new Error("Location not found");
      const { lat, lon } = geoJson[0];

      const [h, m] = ThrowInTime.split(":").map(Number);
      const hour = m === 30 ? (h + 1) % 24 : h;
      const timeTag = `${String(hour).padStart(2, "0")}:00`;

      const weatherRes = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
          `&start_date=${GameDate}&end_date=${GameDate}&hourly=wind_speed_10m`,
      );
      if (!weatherRes.ok) throw new Error(`Weather API failed (${weatherRes.status})`);

      const weatherJson = await weatherRes.json();
      const { time: times = [], wind_speed_10m: speeds = [] } =
        weatherJson.hourly || {};

      const target = `${GameDate}T${timeTag}`;
      const idx = times.indexOf(target);
      if (idx < 0) throw new Error(`No wind data at ${timeTag}`);

      const speed = Number(speeds[idx]);
      if (Number.isNaN(speed)) throw new Error("Invalid wind speed value");

      setFormData((prev) => ({ ...prev, WindStrength: String(Math.round(speed)) }));
      setWindMessage(`Wind at ${timeTag}: ${Math.round(speed)} km/h`);
    } catch (error) {
      toast.error(error.message || "Failed to fetch wind speed", TOAST_OPTIONS);
    } finally {
      setLoadingWind(false);
    }
  };

  const validateForm = () => {
    const requiredFields = [
      "GameDate",
      "ThrowInTime",
      "Competition",
      "GameType",
      "HomeTeam",
      "HomeTeamColor",
      "AwayTeam",
      "AwayTeamColor",
      "FirstHalfVideoStart",
      "SecondHalfVideoStart",
      "WindAngle",
      "WindStrength",
    ];

    for (const key of requiredFields) {
      if (!String(formData[key] ?? "").trim()) {
        toast.error("All fields are required.", TOAST_OPTIONS);
        return false;
      }
    }

   
    if (!TEAM_COLORS.includes(formData.HomeTeamColor) || !TEAM_COLORS.includes(formData.AwayTeamColor)) {
      toast.error("Team colors must be selected from the list.", TOAST_OPTIONS);
      return false;
    }

    const firstHalfSeconds = hhmmssToSeconds(formData.FirstHalfVideoStart);
    const secondHalfSeconds = hhmmssToSeconds(formData.SecondHalfVideoStart);
    if (Number.isNaN(firstHalfSeconds) || Number.isNaN(secondHalfSeconds)) {
      toast.error("Video start times must be in HH:MM:SS format.", TOAST_OPTIONS);
      return false;
    }
    

    const windStrengthNum = Number(formData.WindStrength);
    if (Number.isNaN(windStrengthNum) || windStrengthNum < 0) {
      toast.error("Wind strength must be a valid positive number.", TOAST_OPTIONS);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const firstHalfSeconds = hhmmssToSeconds(formData.FirstHalfVideoStart);
    const secondHalfSeconds = hhmmssToSeconds(formData.SecondHalfVideoStart);
    const homeTeamInput = formData.HomeTeam.trim();
    const awayTeamInput = formData.AwayTeam.trim();
    const homeTeamKey = normalizeTeamName(homeTeamInput);
    const awayTeamKey = normalizeTeamName(awayTeamInput);
    const homeTeamName = teamLookup.get(homeTeamKey) || homeTeamInput;
    const awayTeamName = teamLookup.get(awayTeamKey) || awayTeamInput;
    const missingTeams = [];

    if (!teamLookup.has(homeTeamKey)) missingTeams.push(homeTeamName);
    if (!teamLookup.has(awayTeamKey) && awayTeamKey !== homeTeamKey) missingTeams.push(awayTeamName);

    try {
      for (const teamName of missingTeams) {
        await createSquadData({
          playerName: `${teamName}1`,
          team: teamName,
          position: DEFAULT_NEW_PLAYER_POSITION,
        }).unwrap();
      }

      const res = await createGame({
        GameDate: formData.GameDate,
        ThrowInTime: `${formData.ThrowInTime}:00`,
        HomeTeam: homeTeamName,
        AwayTeam: awayTeamName,
        Competition: formData.Competition,
        GameType: formData.GameType,
        WindAngle: formData.WindAngle,
        WindStrength: formData.WindStrength,
        HomeTeamColor: formData.HomeTeamColor,
        AwayTeamColor: formData.AwayTeamColor,
        FirstHalfVideoTime: firstHalfSeconds,
        SecondHalfVideoTime: secondHalfSeconds,
      }).unwrap();
      onHide();

      toast.success("Game Create Successfully!", TOAST_OPTIONS);
      // If CSV files were staged, upload them now
      if (pendingCsvFiles.length) {
        let lastResult = null;
        for (let i = 0; i < pendingCsvFiles.length; i++) {
          const formData = new FormData();
          formData.append('file', pendingCsvFiles[i]);
          formData.append('gameId', res._id);
          formData.append('finalize', i === pendingCsvFiles.length - 1);
          lastResult = await uploadGameCSV(formData).unwrap();
        }
        setCsvResult(lastResult); // Show result modal
        setPendingCsvFiles([]);
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to add game.", TOAST_OPTIONS);
    }
  };

  const windStrengthValue = useMemo(
    () => (formData.WindStrength === "" ? "" : Number(formData.WindStrength)),
    [formData.WindStrength],
  );
  const firstHalfSecondsLive = hhmmssToSeconds(formData.FirstHalfVideoStart);
  const secondHalfSecondsLive = hhmmssToSeconds(formData.SecondHalfVideoStart);
  const halfTimeMismatchWarning =
    !Number.isNaN(firstHalfSecondsLive) &&
    !Number.isNaN(secondHalfSecondsLive) &&
    secondHalfSecondsLive < firstHalfSecondsLive
      ? "Are you sure? 2nd half time is earlier than 1st half."
      : "";

  return (
    <>
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      contentClassName="bg-dark text-white"
    >
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <div className="d-flex w-100 align-items-center justify-content-between me-3">
            <Modal.Title>Add Game</Modal.Title>
            <Button
              variant="outline-light"
              size="sm"
              type="button"
              onClick={startTutorial}
              className="d-inline-flex align-items-center gap-2"
            >
              <FaQuestionCircle />
              Help
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="text-white">
          <Form noValidate onSubmit={handleSubmit}>
            <Row className="g-1" ref={basicsRef}>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    className="w-75"
                    type="date"
                    name="GameDate"
                    value={formData.GameDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Select
                    className="w-75"
                    name="ThrowInTime"
                    value={formData.ThrowInTime}
                    onChange={handleChange}
                    required
                  >
                    {THROW_IN_TIMES.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Competition</Form.Label>
                  <Row className="g-1">
                    {COMPETITIONS.map((competition) => (
                      <Col xs={6} key={competition}>
                        <Form.Check
                          type="radio"
                          id={`competition-${competition.toLowerCase()}`}
                          name="Competition"
                          label={competition}
                          value={competition}
                          checked={formData.Competition === competition}
                          onChange={handleChange}
                          required
                        />
                      </Col>
                    ))}
                  </Row>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Game Type</Form.Label>
                  <div>
                    {GAME_TYPES.map((gameType) => (
                      <Form.Check
                        key={gameType}
                        type="radio"
                        id={`game-type-${gameType.toLowerCase()}`}
                        name="GameType"
                        label={gameType}
                        value={gameType}
                        checked={formData.GameType === gameType}
                        onChange={handleChange}
                        required
                        className="mb-1"
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 align-items-start">
              <Col md={6} ref={homeTeamRef}>
                <h6 className="mt-2 mb-3 text-white pb-1 border-bottom border-secondary">Home Team</h6>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Name</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    name="HomeTeam"
                    value={formData.HomeTeam}
                    onChange={handleChange}
                    placeholder="Enter home team"
                    list="team-suggestions"
                    autoComplete="off"
                    required
                  />
                  {!teamLookup.has(normalizeTeamName(formData.HomeTeam)) && formData.HomeTeam.trim() && (
                    <Form.Text className="text-warning d-block mt-1">
                      Team "{formData.HomeTeam.trim()}" not found. A new team and default player "{formData.HomeTeam.trim()}1" will be created.
                    </Form.Text>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Color</Form.Label>
                  <Form.Select
                    className="w-50"
                    name="HomeTeamColor"
                    value={formData.HomeTeamColor}
                    onChange={handleChange}
                    required
                  >
                    {TEAM_COLORS.map((color) => (
                      <option key={`home-${color}`} value={color}>
                        {formatColorLabel(color)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} className="border-start border-secondary ps-md-4" ref={awayTeamRef}>
                <h6 className="mt-2 mb-3 text-white pb-1 border-bottom border-secondary">Away Team</h6>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Name</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    name="AwayTeam"
                    value={formData.AwayTeam}
                    onChange={handleChange}
                    placeholder="Enter away team"
                    list="team-suggestions"
                    autoComplete="off"
                    required
                  />
                  {!teamLookup.has(normalizeTeamName(formData.AwayTeam)) && formData.AwayTeam.trim() && (
                    <Form.Text className="text-warning d-block mt-1">
                      Team "{formData.AwayTeam.trim()}" not found. A new team and default player "{formData.AwayTeam.trim()}1" will be created.
                    </Form.Text>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light mb-1" style={{ opacity: 0.85 }}>Color</Form.Label>
                  <Form.Select
                    className="w-50"
                    name="AwayTeamColor"
                    value={formData.AwayTeamColor}
                    onChange={handleChange}
                    required
                  >
                    {awayColorOptions.map((color) => (
                      <option key={`away-${color}`} value={color}>
                        {formatColorLabel(color)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div ref={videoTimeRef}>
            <h6 className="mt-3">Video Time (HH:MM:SS)</h6>
            <Row className="g-1">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>1st Half</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    name="FirstHalfVideoStart"
                    value={formData.FirstHalfVideoStart}
                    onChange={handleChange}
                    placeholder="00:00:00"
                    pattern="^\\d{1,2}:[0-5]\\d:[0-5]\\d$"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={5} className="ps-0">
                <Form.Group className="mb-3">
                  <Form.Label>2nd Half</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="text"
                    name="SecondHalfVideoStart"
                    value={formData.SecondHalfVideoStart}
                    onChange={handleChange}
                    placeholder="00:00:00"
                    pattern="^\\d{1,2}:[0-5]\\d:[0-5]\\d$"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            {halfTimeMismatchWarning && (
              <Form.Text className="text-warning d-block small mt-n2 mb-2">
                {halfTimeMismatchWarning}
              </Form.Text>
            )}
            </div>

            <div ref={windRef}>
            <h6 className="mt-3">Wind Details</h6>
            <Row className="g-3 align-items-start">
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>Wind Direction</Form.Label>
                  <Form.Select
                    className="w-50"
                    name="WindAngle"
                    value={formData.WindAngle}
                    onChange={handleChange}
                    required
                  >
                    {WIND_ANGLES.map((angle) => (
                      <option key={angle} value={angle}>
                        {angle}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Wind Strength (km/h)</Form.Label>
                  <Form.Control
                    className="w-50"
                    type="number"
                    step="1"
                    min="0"
                    name="WindStrength"
                    value={Number.isNaN(windStrengthValue) ? "" : windStrengthValue}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={7} className="ps-0">
                <WindDirectionPreview
                  angle={formData.WindAngle}
                  homeTeam={formData.HomeTeam}
                />
              </Col>
            </Row>

            <div className="mt-0 pt-2 border-top border-secondary">
              <div className="mb-2 d-flex align-items-baseline gap-2">
              <h6
                className="mb-0"
                style={{ cursor: "pointer" }}
                onClick={() => setShowWindLookup((v) => !v)}
              >
                Find Wind Speed from Location (Optional)
              </h6>
              <Button
                variant="link"
                className="p-0 text-light d-inline-flex align-items-center"
                type="button"
                onClick={() => setShowWindLookup((v) => !v)}
                aria-label={showWindLookup ? "Hide wind speed lookup" : "Show wind speed lookup"}
              >
                {showWindLookup ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              </Button>
              </div>
            </div>
            {showWindLookup && (
              <>
                <Row className="align-items-end">
                  <Col md={5}>
                    <Form.Group className="mb-2">
                      <Form.Label>County</Form.Label>
                      <Form.Control
                        className="w-50"
                        type="text"
                        name="County"
                        value={formData.County}
                        onChange={handleChange}
                        placeholder="e.g. Dublin"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-2">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        className="w-50"
                        type="text"
                        name="Town"
                        value={formData.Town}
                        onChange={handleChange}
                        placeholder="e.g. Dublin"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-2">
                      <Button
                        variant="outline-warning"
                        className="px-3 fw-semibold"
                        type="button"
                        onClick={handleGetWindSpeed}
                        disabled={loadingWind}
                      >
                        {loadingWind ? "Finding..." : "Find"}
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
                {windMessage && (
                  <Form.Text className="text-warning d-block mt-1 mb-2">
                    {windMessage}
                  </Form.Text>
                )}
              </>
            )}
            </div>

            <div className="mt-0 pt-2 border-top border-secondary" ref={csvRef}>
              <div className="mb-2 d-flex align-items-baseline gap-2">
              <h6
                className="mb-0"
                style={{ cursor: "pointer" }}
                onClick={() => setShowCsvUpload((v) => !v)}
              >
                Upload Shot CSV (Optional)
              </h6>
              <Button
                variant="link"
                className="p-0 text-light d-inline-flex align-items-center"
                type="button"
                onClick={() => setShowCsvUpload((v) => !v)}
                aria-label={showCsvUpload ? "Hide CSV upload options" : "Show CSV upload options"}
              >
                {showCsvUpload ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              </Button>
              </div>
            </div>
            {showCsvUpload && (
              <div className="mb-3">
                <p className="text-light mb-2">
                  Add a CSV file. It will be uploaded after the game is created.
                </p>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    variant="outline-warning"
                    className="fw-semibold"
                    type="button"
                    onClick={() => setShowCsvModal(true)}
                    disabled={!formData.HomeTeam || !formData.AwayTeam}
                  >
                    {pendingCsvFiles.length ? "Manage CSV File" : "Add CSV File"}
                  </Button>
                  {pendingCsvFiles.length > 0 && (
                    <>
                      <span className="text-info small">
                        {pendingCsvFiles.length} file{pendingCsvFiles.length === 1 ? "" : "s"} queued
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        type="button"
                        onClick={() => setPendingCsvFiles([])}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </div>
                {!formData.HomeTeam || !formData.AwayTeam ? (
                  <Form.Text className="text-warning d-block mt-2">
                    Enter Home Team and Away Team first to stage CSV files.
                  </Form.Text>
                ) : null}
              </div>
            )}
            <div className="mt-3 pt-3 mb-3 border-top border-secondary" />

            <Button type="submit" variant="warning" className="text-dark fw-semibold ms-2">
              Add Game
            </Button>
          </Form>
          <datalist id="team-suggestions">
            {awayTeamOptions.map((team) => (
              <option key={`team-option-${team}`} value={team} />
            ))}
          </datalist>
        </Modal.Body>
      </Modal>
      {showTutorial && (
        <>
          {tutorialRect ? (
            <>
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: tutorialRect.top,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: 0,
                  width: tutorialRect.left,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: tutorialRect.left + tutorialRect.width,
                  width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                  height: tutorialRect.height,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: "fixed",
                  top: tutorialRect.top + tutorialRect.height,
                  left: 0,
                  width: "100vw",
                  height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                  background: "rgba(8, 12, 18, 0.72)",
                  zIndex: 1060,
                }}
              />
              <div
                style={{
                  position: "fixed",
                  top: tutorialRect.top,
                  left: tutorialRect.left,
                  width: tutorialRect.width,
                  height: tutorialRect.height,
                  border: "2px solid #f59e0b",
                  borderRadius: 12,
                  pointerEvents: "none",
                  zIndex: 1061,
                  transition:
                    "top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease",
                }}
              />
              <Card className="border-warning" style={getTutorialCardStyle()}>
                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>
                    {tutorialStepIndex + 1} / {tutorialSteps.length}{" "}
                    {tutorialSteps[tutorialStepIndex].title}
                  </strong>
                  <Button
                    variant="link"
                    className="text-dark p-0 text-decoration-none fw-semibold"
                    onClick={closeTutorial}
                  >
                    Close
                  </Button>
                </Card.Header>
                <Card.Body className="bg-dark text-white">
                  <p className="mb-3">{tutorialSteps[tutorialStepIndex].body}</p>
                  <div className="d-flex justify-content-between gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      disabled={tutorialStepIndex === 0}
                      onClick={prevTutorialStep}
                    >
                      Back
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="text-dark fw-semibold"
                      onClick={nextTutorialStep}
                    >
                      {tutorialStepIndex === tutorialSteps.length - 1 ? "Finish" : "Next"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div
              onClick={closeTutorial}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(8, 12, 18, 0.72)",
                zIndex: 1060,
              }}
            />
          )}
        </>
      )}

      <GameCSVModal
        show={showCsvModal}
        onHide={() => setShowCsvModal(false)}
        pendingFiles={pendingCsvFiles}
        setPendingFiles={setPendingCsvFiles}
        homeTeam={formData.HomeTeam}
        awayTeam={formData.AwayTeam}
        gameType={formData.GameType}
        mode="add"
      />

      {/* Result Modal */}
      <Modal show={!!csvResult} onHide={() => setCsvResult(null)} centered contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title>CSV Upload Result</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-white">
          {csvResult && (
            <div className="csv-result-shell">
              <Alert className="app-empty-state mb-3">
                {csvResult.message || 'Upload successful!'}
              </Alert>

              <div className="csv-result-stats mb-3">
                <div className="csv-result-stat">
                  <div className="csv-result-stat-label">Shots Added</div>
                  <div className="csv-result-stat-value">
                    {csvResult.shotsAdded ?? csvResult.insertedCount ?? 0}
                  </div>
                </div>
                <div className="csv-result-stat">
                  <div className="csv-result-stat-label">New Players</div>
                  <div className="csv-result-stat-value">
                    {csvResult.newPlayers?.length || 0}
                  </div>
                </div>
              </div>

              <div className="csv-result-section">
                <div className="csv-result-section-title">Players Created</div>
                {csvResult.newPlayers && csvResult.newPlayers.length ? (
                  <ul className="csv-result-player-list mb-0">
                    {csvResult.newPlayers.map((p, i) => (
                      <li key={i} className="csv-result-player-item">
                        <span className="csv-result-player-name">{p.playerName}</span>
                        <span className="csv-result-player-team">{p.team}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-secondary">No new players were created.</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <Button variant="warning" className="text-dark fw-semibold" onClick={() => setCsvResult(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      </>
  );
};

export default AddGameModal;

